import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Headphones, Building2, Megaphone, Sparkles, Play, Pause, RotateCcw, ArrowRight, PhoneForwarded, Users, GitBranch, Bot, Workflow, Globe } from 'lucide-react';
// =================== Stage ===================
const STAGE_W = 1280;
const STAGE_H = 720;
const CENTER = {
  x: 640,
  y: 360
};
const CARD_W = 240;
const CARD_H = 78;
const VEXU_W = 320;
const VEXU_H = 120;
// =================== Source numbers (traditional) ===================
type NumCard = {
  id: string;
  label: string;
  number: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  startX: number;
  startY: number;
};
const NUMBERS: NumCard[] = [{
  id: 'sales',
  label: 'Sales Line',
  number: '+971 4 555 0100',
  icon: Phone,
  startX: 230,
  startY: 175
}, {
  id: 'support',
  label: 'Support Line',
  number: '+971 4 555 0200',
  icon: Headphones,
  startX: 230,
  startY: 300
}, {
  id: 'branch',
  label: 'Branch · Abu Dhabi',
  number: '+971 2 555 0300',
  icon: Building2,
  startX: 230,
  startY: 425
}, {
  id: 'mkt',
  label: 'Campaign · Q4',
  number: '+971 800 555 040',
  icon: Megaphone,
  startX: 230,
  startY: 550
}];
// Routing boxes on the right (traditional)
const ROUTERS = [{
  id: 'r1',
  label: 'Carrier A · IVR 1',
  x: 1010,
  y: 195
}, {
  id: 'r2',
  label: 'IVR 2 · Setup B',
  x: 1010,
  y: 290
}, {
  id: 'r3',
  label: 'Carrier B',
  x: 1010,
  y: 385
}, {
  id: 'r4',
  label: 'Routing Tree',
  x: 1010,
  y: 480
}, {
  id: 'r5',
  label: 'Marketing Tracker',
  x: 1010,
  y: 575
}];
// Each number → 2 routing boxes (creating intentional cross-overs to convey chaos cleanly)
const ROUTING_LINKS: Array<[string, string]> = [['sales', 'r1'], ['sales', 'r2'], ['support', 'r2'], ['support', 'r3'], ['branch', 'r3'], ['branch', 'r4'], ['mkt', 'r4'], ['mkt', 'r5']];
// =================== Chapter 5 satellites (scale viz) ===================
const SATELLITES = [{
  id: 'depts',
  label: 'Departments',
  icon: Users,
  x: 640,
  y: 130,
  dx: 0,
  dy: -230,
  sub: ['Sales', 'Support', 'Billing']
}, {
  id: 'branches',
  label: 'Branches',
  icon: GitBranch,
  x: 985,
  y: 360,
  dx: 345,
  dy: 0,
  sub: ['Dubai', 'Abu Dhabi', 'Sharjah']
}, {
  id: 'agents',
  label: 'AI Agents',
  icon: Bot,
  x: 640,
  y: 590,
  dx: 0,
  dy: 230,
  sub: ['Aria', 'Echo', 'Nova']
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  x: 295,
  y: 360,
  dx: -345,
  dy: 0,
  sub: ['Triage', 'Booking', 'Follow-up']
}] as const;
// =================== Chapter 6 regional dots — 7 Emirates around VEXU ===================
const REGIONS: Array<{
  id: string;
  label: string;
  x: number;
  y: number;
  // Where to place the text label relative to the dot (so labels never collide with VEXU)
  labelAnchor: 'top' | 'right' | 'bottom' | 'left';
}> = [{
  id: 'rak',
  label: 'Ras Al Khaimah',
  x: 430,
  y: 150,
  labelAnchor: 'top'
}, {
  id: 'uaq',
  label: 'Umm Al Quwain',
  x: 760,
  y: 150,
  labelAnchor: 'top'
}, {
  id: 'ajm',
  label: 'Ajman',
  x: 1010,
  y: 230,
  labelAnchor: 'right'
}, {
  id: 'shj',
  label: 'Sharjah',
  x: 1040,
  y: 360,
  labelAnchor: 'right'
}, {
  id: 'dxb',
  label: 'Dubai',
  x: 1010,
  y: 500,
  labelAnchor: 'right'
}, {
  id: 'auh',
  label: 'Abu Dhabi',
  x: 410,
  y: 580,
  labelAnchor: 'bottom'
}, {
  id: 'fuj',
  label: 'Fujairah',
  x: 250,
  y: 290,
  labelAnchor: 'left'
}];
// =================== Time markers ===================
const T_TRAD_IN = 0.4;
const T_CONVERGE = 5.0;
const T_VEXU_REVEAL = 9.0;
const T_FORWARD = 14.0;
const T_SCALE = 19.0;
const T_MAP = 25.0;
const T_IMPACT = 30.0;
const T_FOLLOWUP = 33.0;
const TOTAL_DURATION = 38;
// Convergence duration (chapter 2)
const CONVERGE_DURATION = T_VEXU_REVEAL - T_CONVERGE - 0.5; // 3.5s
// =================== Caption per phase ===================
function getCaption(t: number): {
  id: string;
  text: string;
} {
  if (t < T_CONVERGE) return {
    id: 'trad',
    text: 'Traditionally — multiple numbers, multiple setups.'
  };
  if (t < T_VEXU_REVEAL) return {
    id: 'conv',
    text: 'Everything converges to one.'
  };
  if (t < T_FORWARD) return {
    id: 'reveal',
    text: 'With VEXU — one number.'
  };
  if (t < T_SCALE) return {
    id: 'fwd',
    text: "Forward once. That's it."
  };
  if (t < T_MAP) return {
    id: 'scale',
    text: 'Scale instantly — across your entire operation.'
  };
  if (t < T_IMPACT) return {
    id: 'map',
    text: 'One unified entry point — everywhere you operate.'
  };
  return {
    id: 'impact',
    text: 'The new standard.'
  };
}
// =================== Main ===================
export const SLIDE_4_DURATION = TOTAL_DURATION;
export function PresentationSlide4({
  externalElapsed,
  hideChrome
}: {
  externalElapsed?: number;
  hideChrome?: boolean;
} = {}) {
  const isControlled = externalElapsed !== undefined;
  const [runKey, setRunKey] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [internalElapsed, setElapsed] = useState(0);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  useEffect(() => {
    if (isControlled) return;
    startedAtRef.current = Date.now();
    pausedAtRef.current = 0;
    setElapsed(0);
  }, [runKey, isControlled]);
  useEffect(() => {
    if (isControlled) return;
    if (!playing) {
      pausedAtRef.current = internalElapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const t = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(Math.min(t, TOTAL_DURATION));
      if (t < TOTAL_DURATION) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, runKey, isControlled]);
  const elapsed = isControlled ? Math.max(0, Math.min(externalElapsed!, TOTAL_DURATION)) : internalElapsed;
  const caption = getCaption(elapsed);
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F] text-white font-sans flex items-center justify-center" data-id="element-1795">
      {/* Backgrounds */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#06080F] via-[#0A0F1F] to-[#06080F]" data-id="element-1796" />
      <motion.div className="absolute inset-0" animate={{
      background: ['radial-gradient(circle at 30% 40%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)', 'radial-gradient(circle at 70% 30%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 30% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)', 'radial-gradient(circle at 30% 40%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)']
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1797" />
      <div className="absolute inset-0 opacity-[0.10]" style={{
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)',
      backgroundSize: '36px 36px'
    }} data-id="element-1798" />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.7) 100%)'
    }} data-id="element-1799" />

      {/* Top controls */}
      {!hideChrome && <div className="absolute top-6 right-6 z-50 flex items-center gap-2" data-id="element-1800">
          <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1801">
            {playing ? <>
                <Pause className="w-3.5 h-3.5" data-id="element-1802" /> Pause
              </> : <>
                <Play className="w-3.5 h-3.5" data-id="element-1803" /> Play
              </>}
          </button>
          <button onClick={() => {
        setRunKey(k => k + 1);
        setPlaying(true);
      }} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1804">
            <RotateCcw className="w-3.5 h-3.5" data-id="element-1805" /> Replay
          </button>
        </div>}

      {/* Chapter indicator (hidden — replaced by deck-level Vexu.ai brand) */}
      <motion.div initial={{
      opacity: 0,
      x: -12
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.6
    }} className="absolute top-6 left-6 z-50 hidden" data-id="element-1806">
        <div className="flex items-center gap-3" data-id="element-1807">
          <div className="w-8 h-px bg-emerald-400/70" data-id="element-1808" />
          <div data-id="element-1809">
            <div className="text-[10px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1810">
              Slide 04 · One Number
            </div>
            <div className="text-sm font-display text-white/90 tracking-tight mt-0.5" data-id="element-1811">
              Unified Telephony
            </div>
          </div>
        </div>
      </motion.div>

      <Stage elapsed={elapsed} caption={caption} data-id="element-1812" />

      {/* Bottom progress bar */}
      {!hideChrome && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-6" data-id="element-1813">
          <div className="h-1 rounded-full bg-white/[0.08] overflow-hidden" data-id="element-1814">
            <motion.div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300" animate={{
          width: `${elapsed / TOTAL_DURATION * 100}%`
        }} transition={{
          duration: 0.1,
          ease: 'linear'
        }} data-id="element-1815" />
          </div>
          <div className="flex items-center justify-between text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase mt-2.5" data-id="element-1816">
            <span data-id="element-1817">Slide 04 · The Unified Number</span>
            <span className="font-mono" data-id="element-1818">
              {fmt(elapsed)} / {fmt(TOTAL_DURATION)}
            </span>
          </div>
        </div>}
    </div>;
}
function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
// =================== STAGE ===================
function Stage({
  elapsed,
  caption
}: {
  elapsed: number;
  caption: {
    id: string;
    text: string;
  };
}) {
  // Phase flags
  const tradPhase = elapsed >= T_TRAD_IN && elapsed < T_VEXU_REVEAL;
  const showVexu = elapsed >= T_VEXU_REVEAL;
  const forwardPhase = elapsed >= T_FORWARD && elapsed < T_SCALE;
  const scalePhase = elapsed >= T_SCALE && elapsed < T_MAP;
  const mapPhase = elapsed >= T_MAP && elapsed < T_IMPACT;
  const impactPhase = elapsed >= T_IMPACT;
  // Convergence progress for traditional cards (0 = at start, 1 = at center)
  const convergeT = elapsed < T_CONVERGE ? 0 : Math.min(1, (elapsed - T_CONVERGE) / CONVERGE_DURATION);
  // Routing line dissolution (0 = visible, 1 = gone)
  const linesDissolve = elapsed < T_CONVERGE - 0.3 ? 0 : Math.min(1, (elapsed - (T_CONVERGE - 0.3)) / 1.2);
  // Routers fade out as we converge
  const routersFade = linesDissolve;
  return <motion.div initial={{
    opacity: 0,
    scale: 0.96
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.8,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative" style={{
    width: 'min(100vw, calc(100vh * 16 / 9))',
    height: 'min(100vh, calc(100vw * 9 / 16))',
    maxWidth: '100vw',
    maxHeight: '100vh'
  }} data-id="element-1819">
      <div className="absolute inset-0" data-id="element-1820">
        <div className="absolute" style={{
        width: STAGE_W,
        height: STAGE_H,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) scale(var(--stage-scale))',
        transformOrigin: 'center center'
      }} data-id="element-1821">
          <ScaleVar data-id="element-1822" />

          {/* TOP CAPTION */}
          <div className="absolute" style={{
          top: 24,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 6
        }} data-id="element-1823">
            <AnimatePresence mode="wait" data-id="element-1824">
              <motion.div key={caption.id} initial={{
              opacity: 0,
              y: 12,
              filter: 'blur(8px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              y: -12,
              filter: 'blur(8px)'
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="px-7 py-2.5 rounded-full bg-white/[0.06] backdrop-blur-2xl border border-white/15 shadow-[0_8px_40px_rgba(0,0,0,0.5)]" data-id="element-1825">
                <p className="font-display text-lg md:text-xl text-white tracking-tight whitespace-nowrap" data-id="element-1826">
                  {caption.text}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Subtle abstract map glow during chapter 6 */}
          <AnimatePresence data-id="element-1827">
            {mapPhase && <motion.div key="map" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 1.0
          }} className="absolute inset-0 pointer-events-none" style={{
            zIndex: 1
          }} data-id="element-1828">
                <MapGlow data-id="element-1829" />
              </motion.div>}
          </AnimatePresence>

          {/* SVG layer for connection lines */}
          <svg className="absolute pointer-events-none" width={STAGE_W} height={STAGE_H} viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} style={{
          top: 0,
          left: 0,
          overflow: 'visible',
          zIndex: 2
        }} data-id="element-1830">
            {/* CHAPTER 1+2: routing branching lines (faint, dissolve during convergence) */}
            {tradPhase && ROUTING_LINKS.map(([numId, routerId], i) => {
            const num = NUMBERS.find(n => n.id === numId)!;
            const router = ROUTERS.find(r => r.id === routerId)!;
            // Card converges, so its current X/Y interpolates
            const cx = num.startX + (CENTER.x - num.startX) * convergeT;
            const cy = num.startY + (CENTER.y - num.startY) * convergeT;
            const x1 = cx + CARD_W / 2;
            const y1 = cy;
            const x2 = router.x - 110;
            const y2 = router.y;
            const opacity = (1 - linesDissolve) * 0.55;
            return <g key={`link-${i}`} data-id="element-1831">
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#10D96A" strokeWidth={5} strokeOpacity={opacity * 0.18} strokeLinecap="round" data-id="element-1832" />
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#34D399" strokeWidth={1.4} strokeOpacity={opacity * 0.85} strokeLinecap="round" strokeDasharray="3 4" data-id="element-1833" />
                  </g>;
          })}

            {/* CHAPTER 4: Business → VEXU connection (forward arrow line) */}
            {forwardPhase && <ForwardLine elapsed={elapsed} data-id="element-1834" />}

            {/* CHAPTER 5: Scale satellites — lines from VEXU to each satellite */}
            {scalePhase && SATELLITES.map((sat, i) => {
            const t0 = T_SCALE + 0.3 + i * 0.25;
            const visible = elapsed >= t0;
            const length = Math.hypot(sat.x - CENTER.x, sat.y - CENTER.y);
            return <g key={`sat-line-${sat.id}`} data-id="element-1835">
                    <motion.line x1={CENTER.x} y1={CENTER.y} x2={sat.x} y2={sat.y} stroke="#10D96A" strokeWidth={6} strokeOpacity={0.16} fill="none" strokeLinecap="round" strokeDasharray={length} initial={{
                strokeDashoffset: length,
                opacity: 0
              }} animate={{
                strokeDashoffset: visible ? 0 : length,
                opacity: visible ? 1 : 0
              }} transition={{
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-1836" />
                    <motion.line x1={CENTER.x} y1={CENTER.y} x2={sat.x} y2={sat.y} stroke="#34D399" strokeWidth={2} fill="none" strokeLinecap="round" strokeOpacity={0.9} strokeDasharray={length} initial={{
                strokeDashoffset: length,
                opacity: 0
              }} animate={{
                strokeDashoffset: visible ? 0 : length,
                opacity: visible ? 1 : 0
              }} transition={{
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-1837" />
                  </g>;
          })}

            {/* CHAPTER 6: Map — VEXU connections to regional dots */}
            {mapPhase && REGIONS.map((reg, i) => {
            const t0 = T_MAP + 0.4 + i * 0.18;
            const visible = elapsed >= t0;
            const length = Math.hypot(reg.x - CENTER.x, reg.y - CENTER.y);
            return <g key={`reg-${reg.id}`} data-id="element-1838">
                    <motion.line x1={CENTER.x} y1={CENTER.y} x2={reg.x} y2={reg.y} stroke="#10D96A" strokeWidth={1.5} strokeOpacity={0.6} fill="none" strokeLinecap="round" strokeDasharray={length} initial={{
                strokeDashoffset: length,
                opacity: 0
              }} animate={{
                strokeDashoffset: visible ? 0 : length,
                opacity: visible ? 0.8 : 0
              }} transition={{
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1]
              }} data-id="element-1839" />
                    {/* Region dot */}
                    <motion.circle cx={reg.x} cy={reg.y} r={6} fill="#10D96A" initial={{
                opacity: 0,
                scale: 0
              }} animate={{
                opacity: visible ? 1 : 0,
                scale: visible ? 1 : 0
              }} transition={{
                duration: 0.4,
                delay: visible ? 0.3 : 0
              }} data-id="element-1840" />
                    <motion.circle cx={reg.x} cy={reg.y} r={6} fill="none" stroke="#10D96A" strokeOpacity={0.5} initial={{
                scale: 0.5,
                opacity: 0
              }} animate={{
                scale: visible ? [1, 1.8, 1] : 0.5,
                opacity: visible ? [0.6, 0, 0.6] : 0
              }} transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeOut'
              }} data-id="element-1841" />
                  </g>;
          })}
          </svg>

          {/* CHAPTER 6: Emirate name labels (rendered as HTML for crisp typography) */}
          {mapPhase && REGIONS.map((reg, i) => {
          const t0 = T_MAP + 0.4 + i * 0.18;
          const visible = elapsed >= t0;
          // Offset the label away from VEXU center based on its anchor
          const off = 18;
          const tx = reg.labelAnchor === 'right' ? reg.x + off : reg.labelAnchor === 'left' ? reg.x - off : reg.x;
          const ty = reg.labelAnchor === 'top' ? reg.y - off : reg.labelAnchor === 'bottom' ? reg.y + off : reg.y;
          const transform = reg.labelAnchor === 'right' ? 'translate(0, -50%)' : reg.labelAnchor === 'left' ? 'translate(-100%, -50%)' : reg.labelAnchor === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';
          return <motion.div key={`reg-label-${reg.id}`} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: visible ? 1 : 0,
            scale: visible ? 1 : 0.9
          }} transition={{
            duration: 0.5,
            delay: visible ? 0.5 : 0
          }} className="absolute" style={{
            left: tx,
            top: ty,
            transform,
            zIndex: 4
          }} data-id="element-1842">
                  <div className="px-2.5 py-1 rounded-full bg-[#0F172A]/90 backdrop-blur-xl border border-emerald-400/25 whitespace-nowrap shadow-[0_4px_16px_rgba(0,0,0,0.5)]" data-id="element-1843">
                    <span className="text-[10px] font-semibold text-emerald-50 tracking-wide" data-id="element-1844">
                      {reg.label}
                    </span>
                  </div>
                </motion.div>;
        })}

          {/* CHAPTER 1+2: Number cards (converging) */}
          {tradPhase && NUMBERS.map((n, i) => {
          const Icon = n.icon;
          const cx = n.startX + (CENTER.x - n.startX) * convergeT;
          const cy = n.startY + (CENTER.y - n.startY) * convergeT;
          const scale = 1 - convergeT * 0.55;
          const opacity = 1 - convergeT * 0.95;
          return <PositionedNode key={n.id} x={cx} y={cy} data-id="element-1845">
                  <motion.div initial={{
              opacity: 0,
              x: -16
            }} animate={{
              opacity: opacity,
              x: 0,
              scale
            }} transition={{
              duration: 0.5,
              delay: i * 0.08,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" style={{
              width: CARD_W,
              height: CARD_H
            }} data-id="element-1846">
                    <div className="absolute -inset-2.5 bg-emerald-500/[0.10] rounded-2xl blur-xl" data-id="element-1847" />
                    <div className="relative rounded-xl bg-[#0F172A]/95 backdrop-blur-xl border border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.6)] flex items-center gap-3 px-4" style={{
                width: CARD_W,
                height: CARD_H
              }} data-id="element-1848">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-white/15 to-white/[0.03] border border-white/10 flex items-center justify-center shrink-0" data-id="element-1849">
                        <Icon className="w-4 h-4 text-emerald-300" strokeWidth={2} data-id="element-1850" />
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-1851">
                        <div className="text-[10px] font-bold tracking-[0.18em] text-white/45 uppercase truncate" data-id="element-1852">
                          {n.label}
                        </div>
                        <div className="text-[14px] font-semibold text-white tracking-tight font-mono truncate" data-id="element-1853">
                          {n.number}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </PositionedNode>;
        })}

          {/* CHAPTER 1+2: Routing boxes (right side) */}
          {tradPhase && ROUTERS.map((r, i) => <PositionedNode key={r.id} x={r.x} y={r.y} data-id="element-1854">
                <motion.div initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: (1 - routersFade) * 1,
            x: routersFade > 0.4 ? 24 : 0
          }} transition={{
            duration: 0.6,
            delay: i * 0.06,
            ease: [0.16, 1, 0.3, 1]
          }} className="px-3.5 py-2 rounded-lg bg-white/[0.04] border border-white/10 backdrop-blur-xl" style={{
            minWidth: 200
          }} data-id="element-1855">
                  <div className="flex items-center gap-2" data-id="element-1856">
                    <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1857" />
                    <span className="text-[11px] font-semibold text-white/85 tracking-tight" data-id="element-1858">
                      {r.label}
                    </span>
                  </div>
                </motion.div>
              </PositionedNode>)}

          {/* VEXU central card — appears at reveal, persists through chapters 3-6 */}
          {showVexu && !forwardPhase && !impactPhase && <PositionedNode x={CENTER.x} y={CENTER.y} data-id="element-1859">
              <VexuCard appear={elapsed >= T_VEXU_REVEAL} data-id="element-1860" />
            </PositionedNode>}

          {/* CHAPTER 4: Forward layout — Business (left) → VEXU (right) */}
          {forwardPhase && <>
              <PositionedNode x={380} y={CENTER.y} data-id="element-1861">
                <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.7,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" style={{
              width: CARD_W,
              height: CARD_H + 14
            }} data-id="element-1862">
                  <div className="absolute -inset-3 bg-white/5 rounded-2xl blur-xl" data-id="element-1863" />
                  <div className="relative rounded-2xl bg-[#0F172A]/95 backdrop-blur-xl border border-white/20 shadow-[0_8px_40px_rgba(0,0,0,0.6)] flex items-center gap-3 px-5" style={{
                width: CARD_W,
                height: CARD_H + 14
              }} data-id="element-1864">
                    <div className="w-11 h-11 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0" data-id="element-1865">
                      <Phone className="w-5 h-5 text-white" strokeWidth={2.2} data-id="element-1866" />
                    </div>
                    <div className="min-w-0" data-id="element-1867">
                      <div className="text-[10px] font-bold tracking-[0.22em] text-white/55 uppercase" data-id="element-1868">
                        Your Number
                      </div>
                      <div className="text-[15px] font-semibold text-white tracking-tight font-mono" data-id="element-1869">
                        +971 4 555 0142
                      </div>
                    </div>
                  </div>
                </motion.div>
              </PositionedNode>

              <PositionedNode x={900} y={CENTER.y} data-id="element-1870">
                <VexuCard appear={true} data-id="element-1871" />
              </PositionedNode>
            </>}

          {/* CHAPTER 5: Satellites */}
          {scalePhase && SATELLITES.map((sat, i) => {
          const Icon = sat.icon;
          const t0 = T_SCALE + 0.4 + i * 0.25;
          const visible = elapsed >= t0;
          return <PositionedNode key={sat.id} x={sat.x} y={sat.y} data-id="element-1872">
                  <motion.div initial={{
              opacity: 0,
              scale: 0.85
            }} animate={{
              opacity: visible ? 1 : 0,
              scale: visible ? 1 : 0.85
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative" data-id="element-1873">
                    <div className="absolute -inset-2 bg-emerald-500/15 rounded-2xl blur-lg" data-id="element-1874" />
                    <div className="relative px-4 py-3 rounded-xl bg-[#0F172A]/95 backdrop-blur-xl border border-emerald-400/30 shadow-[0_8px_24px_rgba(0,0,0,0.55)] flex items-center gap-2.5" data-id="element-1875">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0" data-id="element-1876">
                        <Icon className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.2} data-id="element-1877" />
                      </div>
                      <div data-id="element-1878">
                        <div className="text-[9px] font-bold tracking-[0.22em] text-emerald-300/85 uppercase" data-id="element-1879">
                          Layer
                        </div>
                        <div className="text-[13px] font-semibold text-white tracking-tight" data-id="element-1880">
                          {sat.label}
                        </div>
                      </div>
                    </div>
                    {/* Sub-nodes (small dots/labels) */}
                    <div className="absolute flex flex-wrap gap-1.5" style={{
                left: '50%',
                top: sat.dy < 0 ? -34 : sat.dy > 0 ? '100%' : '50%',
                transform: sat.dy === 0 ? `translate(${sat.dx > 0 ? '110%' : '-110%'}, -50%)` : `translateX(-50%) translateY(${sat.dy < 0 ? '-100%' : '8px'})`,
                whiteSpace: 'nowrap'
              }} data-id="element-1881">
                      {sat.sub.map((s, j) => <motion.div key={s} initial={{
                  opacity: 0,
                  scale: 0.85
                }} animate={{
                  opacity: visible ? 1 : 0,
                  scale: visible ? 1 : 0.85
                }} transition={{
                  duration: 0.4,
                  delay: visible ? 0.4 + j * 0.1 : 0
                }} className="px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-[9px] font-semibold text-white/75 tracking-wide" data-id="element-1882">
                          {s}
                        </motion.div>)}
                    </div>
                  </motion.div>
                </PositionedNode>;
        })}

          {/* CHAPTER 7: Final impact takeover */}
          <AnimatePresence data-id="element-1883">
            {impactPhase && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.9
          }} className="absolute inset-0 flex items-center justify-center" style={{
            background: 'radial-gradient(circle at center, rgba(6,8,15,0.94) 0%, rgba(6,8,15,0.99) 100%)',
            zIndex: 35
          }} data-id="element-1884">
                <ImpactReveal elapsed={elapsed} data-id="element-1885" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}
// =================== Sub-components ===================
function VexuCard({
  appear
}: {
  appear: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.85
  }} animate={{
    opacity: appear ? 1 : 0,
    scale: appear ? 1 : 0.85
  }} transition={{
    duration: 0.9,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative" style={{
    width: VEXU_W,
    height: VEXU_H
  }} data-id="element-1886">
      <div className="absolute -inset-10 rounded-full" style={{
      background: 'radial-gradient(circle, rgba(16,217,106,0.40) 0%, rgba(16,217,106,0) 70%)',
      filter: 'blur(8px)'
    }} data-id="element-1887" />
      <motion.div animate={{
      scale: [1, 1.12, 1],
      opacity: [0.45, 0, 0.45]
    }} transition={{
      duration: 2.6,
      repeat: Infinity,
      ease: 'easeOut'
    }} className="absolute inset-0 rounded-2xl border border-emerald-300/50" data-id="element-1888" />
      <motion.div animate={{
      scale: [1, 1.22, 1],
      opacity: [0.3, 0, 0.3]
    }} transition={{
      duration: 2.6,
      repeat: Infinity,
      ease: 'easeOut',
      delay: 0.8
    }} className="absolute inset-0 rounded-2xl border border-emerald-300/30" data-id="element-1889" />
      <div className="relative rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-[0_0_70px_rgba(16,217,106,0.55)] border border-emerald-300/50 flex items-center gap-3 px-7" style={{
      width: VEXU_W,
      height: VEXU_H
    }} data-id="element-1890">
        <div className="w-14 h-14 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0" data-id="element-1891">
          <Sparkles className="w-7 h-7 text-white" strokeWidth={2.2} data-id="element-1892" />
        </div>
        <div className="min-w-0" data-id="element-1893">
          <div className="text-[10px] font-bold tracking-[0.32em] text-white/85 uppercase mb-0.5" data-id="element-1894">
            VEXU Unified Number
          </div>
          <div className="text-[22px] font-bold text-white tracking-tight font-mono leading-tight whitespace-nowrap" data-id="element-1895">
            800 VEXU (8388)
          </div>
          <div className="text-[10px] font-medium tracking-[0.2em] text-emerald-100/85 mt-1 uppercase" data-id="element-1896">
            One entry · Everywhere
          </div>
        </div>
      </div>
    </motion.div>;
}
function ForwardLine({
  elapsed
}: {
  elapsed: number;
}) {
  const t = elapsed - T_FORWARD;
  const x1 = 380 + CARD_W / 2;
  const y1 = CENTER.y;
  const x2 = 900 - VEXU_W / 2;
  const y2 = CENTER.y;
  const length = Math.hypot(x2 - x1, y2 - y1);
  return <g data-id="element-1897">
      <motion.line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#10D96A" strokeWidth={9} strokeOpacity={0.18} strokeLinecap="round" strokeDasharray={length} initial={{
      strokeDashoffset: length,
      opacity: 0
    }} animate={{
      strokeDashoffset: t > 0.6 ? 0 : length,
      opacity: t > 0.6 ? 1 : 0
    }} transition={{
      duration: 1.0,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-1898" />
      <motion.line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#6EE7B7" strokeWidth={2.5} strokeLinecap="round" strokeOpacity={0.95} strokeDasharray={length} initial={{
      strokeDashoffset: length,
      opacity: 0
    }} animate={{
      strokeDashoffset: t > 0.6 ? 0 : length,
      opacity: t > 0.6 ? 1 : 0
    }} transition={{
      duration: 1.0,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-1899" />
      {/* Traveling pulse */}
      {t > 1.8 && <motion.circle r={5} fill="#fef3c7" opacity={0.95} animate={{
      cx: [x1, x2],
      cy: [y1, y2],
      opacity: [0, 1, 1, 0]
    }} transition={{
      duration: 1.6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1900" />}
      {/* Forward badge in middle */}
      {t > 1.6 && (() => {
      const mx = (x1 + x2) / 2;
      return <foreignObject x={mx - 50} y={y1 - 14} width={100} height={28} style={{
        overflow: 'visible'
      }} data-id="element-1901">
              <div className="flex items-center justify-center" data-id="element-1902">
                <div className="px-2.5 py-1 rounded-full bg-[#0A0F1F] border border-emerald-400/40 flex items-center gap-1.5" data-id="element-1903">
                  <ArrowRight className="w-3 h-3 text-emerald-400" data-id="element-1904" />
                  <span className="text-[9px] font-bold tracking-[0.18em] text-emerald-300 uppercase" data-id="element-1905">
                    Forward
                  </span>
                </div>
              </div>
            </foreignObject>;
    })()}
    </g>;
}
function MapGlow() {
  // Generate scattered "constellation" dots in the background
  const dots = useMemo(() => {
    const out: Array<{
      x: number;
      y: number;
      r: number;
      o: number;
    }> = [];
    const seed = 7;
    let s = seed;
    const rand = () => {
      s = (s * 9301 + 49297) % 233280;
      return s / 233280;
    };
    for (let i = 0; i < 70; i++) {
      out.push({
        x: rand() * STAGE_W,
        y: rand() * STAGE_H,
        r: 0.6 + rand() * 1.4,
        o: 0.2 + rand() * 0.5
      });
    }
    return out;
  }, []);
  return <div className="relative w-full h-full" data-id="element-1906">
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 50%, rgba(16,217,106,0.10) 0%, rgba(16,217,106,0) 60%)'
    }} data-id="element-1907" />
      <svg className="absolute inset-0" viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} width={STAGE_W} height={STAGE_H} data-id="element-1908">
        {dots.map((d, i) => <motion.circle key={i} cx={d.x} cy={d.y} r={d.r} fill="#10D96A" initial={{
        opacity: 0
      }} animate={{
        opacity: d.o
      }} transition={{
        duration: 1.4,
        delay: 0.3 + i * 0.012
      }} data-id="element-1909" />)}
        {/* Subtle abstract land mass arcs */}
        <motion.ellipse cx={640} cy={360} rx={520} ry={250} fill="none" stroke="#10D96A" strokeOpacity={0.08} strokeWidth={1} initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 0.4
      }} transition={{
        duration: 2.0,
        ease: 'easeOut'
      }} data-id="element-1910" />
        <motion.ellipse cx={640} cy={360} rx={420} ry={200} fill="none" stroke="#10D96A" strokeOpacity={0.06} strokeWidth={1} initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 0.4
      }} transition={{
        duration: 2.0,
        delay: 0.4,
        ease: 'easeOut'
      }} data-id="element-1911" />
      </svg>
      <div className="absolute top-12 left-1/2 -translate-x-1/2 flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] text-emerald-300/70 uppercase" data-id="element-1912">
        <Globe className="w-3.5 h-3.5" data-id="element-1913" /> UAE coverage · All 7 Emirates
      </div>
    </div>;
}
// =================== Impact takeover ===================
function ImpactReveal({
  elapsed
}: {
  elapsed: number;
}) {
  const t = elapsed - T_IMPACT;
  const negatives = [{
    text: 'No complex routing.',
    show: 0.0
  }, {
    text: 'No telecom limitations.',
    show: 0.7
  }, {
    text: 'No scaling barriers.',
    show: 1.4
  }];
  const tFollow = elapsed - T_FOLLOWUP;
  return <div className="text-center px-6" data-id="element-1914">
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="flex items-center gap-2 justify-center mb-8" data-id="element-1915">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" data-id="element-1916" />
        <span className="text-[11px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1917">
          The new standard
        </span>
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" data-id="element-1918" />
      </motion.div>

      <div className="flex flex-col items-center gap-2 mb-12" data-id="element-1919">
        {negatives.map((w, i) => {
        const visible = t >= w.show;
        return <motion.span key={i} initial={{
          opacity: 0,
          y: 24,
          scale: 0.92,
          filter: 'blur(10px)'
        }} animate={{
          opacity: visible ? 1 : 0,
          y: visible ? 0 : 24,
          scale: visible ? 1 : 0.92,
          filter: visible ? 'blur(0px)' : 'blur(10px)'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white" style={{
          textShadow: '0 0 30px rgba(16,217,106,0.4)'
        }} data-id="element-1920">
              {w.text}
            </motion.span>;
      })}
      </div>

      <div className="flex items-baseline justify-center gap-3 md:gap-5 flex-wrap" data-id="element-1921">
        <motion.span initial={{
        opacity: 0,
        y: 16,
        filter: 'blur(8px)'
      }} animate={{
        opacity: tFollow >= 0 ? 1 : 0,
        y: tFollow >= 0 ? 0 : 16,
        filter: tFollow >= 0 ? 'blur(0px)' : 'blur(8px)'
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} className="font-display text-3xl md:text-4xl text-white/60 italic tracking-tight" data-id="element-1922">
          One number…
        </motion.span>
        <motion.span initial={{
        opacity: 0,
        y: 16,
        filter: 'blur(8px)'
      }} animate={{
        opacity: tFollow >= 1.0 ? 1 : 0,
        y: tFollow >= 1.0 ? 0 : 16,
        filter: tFollow >= 1.0 ? 'blur(0px)' : 'blur(8px)'
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} className="font-display text-4xl md:text-5xl font-bold tracking-tight" style={{
        background: 'linear-gradient(135deg, #10D96A 0%, #6EE7B7 60%, #ffffff 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }} data-id="element-1923">
          …infinite possibilities.
        </motion.span>
      </div>
    </div>;
}
// =================== Helpers ===================
function ScaleVar() {
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const scale = Math.min(w / STAGE_W, h / STAGE_H, 1);
      document.documentElement.style.setProperty('--stage-scale', String(scale));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return null;
}
function PositionedNode({
  x,
  y,
  children
}: {
  x: number;
  y: number;
  children: React.ReactNode;
}) {
  return <div className="absolute" style={{
    left: x,
    top: y,
    transform: 'translate(-50%, -50%)',
    // Explicit z-index ensures cards always paint above the SVG line layer.
    // Without this, cards default to z-auto and the SVG (z-index 2) would draw on top.
    zIndex: 5
  }} data-id="element-1924">
      {children}
    </div>;
}